/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Toast, GoTop } from '@components/atoms'

import '@mdi/font/css/materialdesignicons.css'
import { GlobalComponentsContextProvider } from '@contexts'
import { Logger } from '@utils'
import {
	useAppDispatch,
	configActions,
	useAppSelector,
	ConfigStateType,
} from '@redux/index'
import config from '@configs'
import { isMobile } from 'react-device-detect'

interface PropsType {
	children: React.ReactNode
}

export const AppWrapper: React.FC<PropsType> = props => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const storeSystemName: string = useAppSelector(state => state.config.systemName)
	const toast: ConfigStateType['toast'] = useAppSelector(state => state.config.toast)

	// toast state
	const [toastMessage, setToastMessage] = React.useState<string>('')
	const [showToast, setShowToast] = React.useState<boolean>(false)
	const [showGoTop, setShowGoTop] = React.useState(false)

	React.useEffect(() => {
		Logger.env()

		window.addEventListener('scroll', handleScroll)
		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	const handleScroll = () => {
		if (window.scrollY > 300) {
			setShowGoTop(true)
		} else {
			setShowGoTop(false)
		}
	}

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	React.useEffect(() => {
		reduxDispatch(configActions.IS_MOBILE(isMobile))
	}, [isMobile])

	React.useEffect(() => {
		const systemEnv = config.get('system')
		const systemName: string = systemEnv.name.replaceAll(/-/g, ' ')
		const systemSubtitle: string = systemEnv.subtitle
		const title = systemName

		document.title = title

		if (storeSystemName !== systemName) {
			reduxDispatch(configActions.SYSTEM_NAME(systemName))
		}

		if (systemSubtitle !== '') {
			reduxDispatch(configActions.SYSTEM_SUBTITLE(systemSubtitle))
		}
	}, [storeSystemName])

	React.useEffect(() => {
		if (toast.isShow) {
			setToastMessage(toast.message)
			setShowToast(true)
			reduxDispatch(configActions.TOAST({ isShow: false, message: '' }))
		}
	}, [toast])

	const onShowToast = (message: string) => {
		setToastMessage(message)
		setShowToast(true)
	}

	return (
		<GlobalComponentsContextProvider onShowToast={onShowToast}>
			<View css={style}>
				{props.children}

				<GoTop show={showGoTop} onClick={scrollToTop} />
				<Toast show={showToast} title={toastMessage} onToggleToast={setShowToast} />
			</View>
		</GlobalComponentsContextProvider>
	)
}

const style = css({})
